import { useCallback, useEffect, useRef, useState } from "react";
import { http, toastApiError, downloadUrl } from "../lib/api";
import { toast } from "../lib/notify";
import { FileSignature, Upload, FolderOpen, ChevronDown, Download, Trash2, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const fmt = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleDateString("tr-TR"); };

// Proje kartı: "Sözleşmeler" düğmesi → Sözleşme Yükle / Kayıtlı Sözleşmeler (liste → tıkla → PDF indir)
export default function ProjectContracts({ projectId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // menu | list | upload
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const ref = useRef(null);
  const load = useCallback(() => http.get(`/projects/${projectId}/contracts`).then((r) => setDocs(r.data)).catch(() => {}), [projectId]);
  useEffect(() => { if (projectId) load(); }, [projectId, load]);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setView("menu"); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const upload = async () => {
    if (!file) return toast.error("PDF dosyası seçin");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await http.post(`/projects/${projectId}/contracts?title=${encodeURIComponent(title)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sözleşme yüklendi"); setFile(null); setTitle(""); await load(); setView("list");
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`"${d.title}" silinsin mi?`)) return;
    try { await http.delete(`/projects/contracts/${d.id}`); toast.success("Silindi"); await load(); } catch (e) { toastApiError(e); }
  };
  const item = "w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-semibold hover:bg-slate-50";
  return (
    <div ref={ref} className="relative" data-testid="proj-contracts">
      <button type="button" onClick={() => { setOpen((o) => !o); setView("menu"); }} data-testid="proj-contracts-btn"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-indigo-300 bg-indigo-50 text-indigo-800 text-xs font-semibold hover:bg-indigo-100">
        <FileSignature className="w-4 h-4" /> Sözleşmeler{docs.length > 0 && <span className="px-1.5 rounded bg-indigo-700 text-white text-[10px]" data-testid="proj-contracts-count">{docs.length}</span>}<ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-1 w-80 bg-white border border-slate-200 rounded-md shadow-xl py-1" data-testid="proj-contracts-menu">
          {view === "menu" && (
            <>
              <button type="button" onClick={() => setView("upload")} data-testid="proj-contracts-upload-opt" className={item}><Upload className="w-4 h-4 text-indigo-600" /> Sözleşme Yükle</button>
              <button type="button" onClick={() => setView("list")} data-testid="proj-contracts-list-opt" className={item}><FolderOpen className="w-4 h-4 text-amber-600" /> Kayıtlı Sözleşmeler <span className="ml-auto text-[10px] text-slate-400">{docs.length}</span></button>
            </>
          )}
          {view !== "menu" && <button type="button" onClick={() => setView("menu")} className="flex items-center gap-1 px-3 py-1 text-[11px] text-slate-500 hover:text-slate-800" data-testid="proj-contracts-back"><ArrowLeft className="w-3 h-3" /> Geri</button>}
          {view === "upload" && (
            <div className="px-3 pb-3 space-y-2" data-testid="proj-contracts-upload">
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sözleşme adı (örn. 2026 Hizmet Sözleşmesi)" data-testid="proj-contract-title" className="w-full h-8 px-2 border border-slate-300 rounded text-xs" />
              <input type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="proj-contract-file" className="w-full text-xs" />
              <button type="button" onClick={upload} disabled={busy || !file} data-testid="proj-contract-upload-btn" className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-indigo-700 text-white text-xs font-semibold hover:bg-indigo-800 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} PDF'i Yükle
              </button>
            </div>
          )}
          {view === "list" && (
            <div className="max-h-72 overflow-auto" data-testid="proj-contracts-list">
              {docs.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">Kayıtlı sözleşme yok.</div>}
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50" data-testid={`proj-contract-${d.id}`}>
                  <a href={downloadUrl(`/projects/contracts/${d.id}/download`)} target="_blank" rel="noreferrer" data-testid={`proj-contract-dl-${d.id}`} className="flex-1 flex items-center gap-2 text-xs min-w-0" title="PDF indir">
                    <Download className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                    <span className="min-w-0"><span className="block font-semibold truncate">{d.title}</span><span className="block text-[10px] text-slate-400">{fmt(d.created_at)} · {(d.size / 1024).toFixed(0)} KB · {d.created_by}</span></span>
                  </a>
                  {isAdmin && <button type="button" onClick={() => remove(d)} data-testid={`proj-contract-del-${d.id}`} className="p-1 rounded text-red-600 hover:bg-red-50" title="Sil"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
